// src/Api/api.js
import axios from "axios";

const API_BASE_URL = "https://quizzyin.com/api/v1";

// Create Axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ✅ Request Interceptor: Attach Bearer token automatically
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ Response Interceptor: Handle 401 and refresh token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const refreshToken = localStorage.getItem("refreshToken");

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      refreshToken
    ) {
      if (isRefreshing) {
        // Wait for the token refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // 🔁 Request new access token using refreshToken
        const res = await axios.post(`${API_BASE_URL}/refresh`, {
          refreshToken,
        });

        const newAccessToken = res.data.accessToken;

        // ✅ Update localStorage
        localStorage.setItem("accessToken", newAccessToken);

        // Update full loginData with new accessToken
        const loginData = JSON.parse(localStorage.getItem("loginData")) || {};
        loginData.accessToken = newAccessToken;
        localStorage.setItem("loginData", JSON.stringify(loginData));

        // Retry all queued requests
        processQueue(null, newAccessToken);

        // Retry original request
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (err) {
        // ❌ Refresh failed — logout
        processQueue(err, null);
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("loginData");

        // Redirect only if not already on login page
        const currentUrl = window.location.pathname;
        if (!currentUrl.includes("/login")) {
          window.location.href = "/login";
        }

        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    // If 401 and no refresh token, or other error
    return Promise.reject(error);
  }
);

// ✅ Common API Methods
export const get = (url) => apiClient.get(url).then((res) => res.data);
export const post = (url, data) =>
  apiClient.post(url, data).then((res) => res.data);
export const put = (url, data) =>
  apiClient.put(url, data).then((res) => res.data);
export const del = (url) => apiClient.delete(url).then((res) => res.data);

// ✅ Export base URL and Axios instance
export { API_BASE_URL };
export default apiClient;
